import moment from 'moment';
import {
  DescriptionItem,
  DeliveryRow,
  DeliveryData,
  DescriptionPayload,
  TradeItem,
} from './DeliveryHistory.types';
import {
  extractGdLinks,
  extractFrontAppLinks,
  extractPdfLinks,
  extractSheetIndexFromDescriptions,
  getTradeCode,
  getPlanCheckRevName,
  resolveTradeDetails,
  resolveChildTrades,
  normalizeTradeName,
} from '../../utils/GenerateQueryString';

export const resolveId = (val: unknown): string => {
  if (!val) return '';
  if (typeof val === 'object') {
    const obj = val as { _id?: unknown; id?: unknown };
    return String(obj._id ?? obj.id ?? '').trim();
  }
  return String(val).trim();
};

export const mapDescriptionItems = (
  descriptions: DescriptionPayload[],
  tasks: Record<string, unknown>[],
  date: string,
  stageName: string,
  isDark: boolean
): DescriptionItem[] =>
  descriptions.map((desc: DescriptionPayload) => {
    const { taskIdStr, tradeName } = resolveTradeDetails(desc, tasks, isDark);
    const createdByStr =
      typeof desc?.createdBy === 'object' && desc?.createdBy
        ? (desc.createdBy as { name?: string; FirstName?: string }).name || (desc.createdBy as { name?: string; FirstName?: string }).FirstName || ''
        : String(desc?.createdBy || '');
    return {
      trade: tradeName || '',
      taskId: taskIdStr,
      date,
      createdAt: desc?.createdAt,
      notes: desc?.name || desc?.notes || '',
      attachment: desc?.attachment || [],
      stageRev: getPlanCheckRevName(desc, stageName),
      sheetNo: desc?.sheetNo || '',
      sheetTitle: desc?.sheetTitle || '',
      discipline: desc?.discipline || '',
      status: desc?.status,
      isUpdated: desc?.isUpdated,
      createdBy: createdByStr,
    };
  });

export const computeDefaultSubTitle = (
  stageName: string,
  descriptions: DescriptionPayload[],
  subDeliveries?: DeliveryData[],
  actualStageName?: string
): string => {
  const descStages = descriptions
    .map(d =>
      (typeof d?.stageId === 'object' && d?.stageId ? d.stageId.temporaryName || d.stageId.name || '' : '') ||
      d?.temporaryName ||
      d?.stageName ||
      ''
    )
    .filter(Boolean);
  const subStages = Array.isArray(subDeliveries)
    ? subDeliveries
        .map(s =>
          (typeof s?.stageId === 'object' && s?.stageId ? s.stageId.temporaryName || s.stageId.name || '' : '') ||
          s?.temporaryName ||
          s?.stageName ||
          s?.title ||
          ''
        )
        .filter(Boolean)
    : [];
  const rawDistinct = Array.from(new Set([stageName, ...descStages, ...subStages].filter(Boolean)));
  const distinctStages = actualStageName && stageName && actualStageName.toLowerCase() !== stageName.toLowerCase()
    ? rawDistinct.filter(s => s.toLowerCase() !== actualStageName.toLowerCase())
    : rawDistinct;
  const distinctTags = Array.from(new Set(descriptions.map(d => getPlanCheckRevName(d, '')).filter(Boolean))).filter(
    t => !distinctStages.includes(t)
  );
  const isStageRevOrPc = /^(rev|revision|pc|plan\s*check)/i.test(stageName.trim());
  const hasRevOrPc =
    isStageRevOrPc ||
    distinctTags.length > 0 ||
    descriptions.some(d => Boolean(d?.revisionId || d?.planCheckId || (d?.stageRev && /^(r\d|pc\d|rev|pc)/i.test(d.stageRev.trim()))));
  const isCombined = (Array.isArray(subDeliveries) && subDeliveries.length > 1) || distinctStages.length > 1 || hasRevOrPc;
  const baseTitle = stageName ? (/delivery/i.test(stageName) ? stageName : `${stageName} Delivery`) : 'Delivery';

  if (isCombined) {
    const isStageContainer = /^(rev|revision|pc|plan\s*check)$/i.test(stageName.trim());
    const stagesToShow = isStageContainer
      ? distinctStages.filter(s => !/^(rev|revision|pc|plan\s*check)$/i.test(s.trim()))
      : distinctStages;
    const combinedStr = stagesToShow.join(', ');
    const parts = [combinedStr, ...distinctTags].filter(Boolean);
    const detailsStr = parts.join(', ');
    return detailsStr ? `Combined Delivery — ${detailsStr}` : 'Combined Delivery';
  }
  return baseTitle;
};

export const formatModalHeading = (title: string): string => {
  if (!title) return '';
  const trimmed = title.trim();
  const dashIndex = trimmed.search(/[—–-]/);
  if (dashIndex !== -1) {
    const prefix = trimmed.slice(0, dashIndex).trim();
    const rest = trimmed.slice(dashIndex + 1).trim();
    if (/combined\s*delivery/i.test(prefix)) {
      const firstStage = rest.split(',')[0]?.trim();
      return firstStage ? `${firstStage} Combined Delivery` : prefix;
    }
    const firstTag = rest.split(',')[0]?.trim();
    return firstTag ? `${prefix} ${firstTag}` : prefix;
  }
  return trimmed;
};

export const buildTableRows = (
  data?: DeliveryData[],
  taskbyproject?: Record<string, unknown>[],
  isDark = false
): DeliveryRow[] => {
  if (!data?.length) return [];
  const taskList = taskbyproject || [];

  const stageGroups: { stageKey: string; deliveries: DeliveryData[] }[] = [];
  const groupMap = new Map<string, DeliveryData[]>();

  data.forEach((delivery, idx) => {
    const sKey =
      resolveId(delivery?.stageId) ||
      resolveId(delivery?.stageDataId) ||
      delivery?.stageId?.name ||
      delivery?.stageName ||
      resolveId(delivery) ||
      `stage_${idx}`;

    if (!groupMap.has(sKey)) {
      const list: DeliveryData[] = [];
      groupMap.set(sKey, list);
      stageGroups.push({ stageKey: sKey, deliveries: list });
    }
    groupMap.get(sKey)!.push(delivery);
  });

  return stageGroups.map((group, idx) => {
    const parentNum = idx + 1;
    const firstDelivery = group.deliveries[0];
    const actualStageName = firstDelivery?.stageId?.name || firstDelivery?.stageName || '';
    const stageName =
      firstDelivery?.stageId?.temporaryName ||
      firstDelivery?.temporaryName ||
      group.deliveries.find(d => d?.stageId?.temporaryName || d?.temporaryName)?.stageId?.temporaryName ||
      group.deliveries.find(d => d?.stageId?.temporaryName || d?.temporaryName)?.temporaryName ||
      actualStageName ||
      '';
    const stageColor = firstDelivery?.stageId?.color || (firstDelivery as { stageColor?: string } | null)?.stageColor || '';
    const parentId = resolveId(firstDelivery?.stageId) || resolveId(firstDelivery) || group.stageKey || String(idx);

    let subCounter = 1;
    const subRows: DeliveryRow[] = [];

    group.deliveries.forEach((delivery) => {
      const dateVal =
        delivery?.payload?.deliveredDate ||
        delivery?.payload?.deliveryDate ||
        delivery?.deliveryDate ||
        delivery?.createdAt;
      const deliveryDate = dateVal ? moment(dateVal).format('MMM DD, YYYY') : '';
      const parentGdLinks = extractGdLinks(delivery);
      const parentFrontAppLinks = extractFrontAppLinks(delivery);
      const parentPdfLinks = extractPdfLinks(delivery);
      const parentPdf = parentPdfLinks[0] || delivery?.payload?.pdfLink || '';

      const descriptions: DescriptionPayload[] =
        delivery?.payload?.deliveryNotes ||
        delivery?.payload?.deliveryNotesToDisplay ||
        delivery?.payload?.Description ||
        delivery?.Description ||
        [];
      const descItems = mapDescriptionItems(descriptions, taskList, deliveryDate, stageName, isDark);
      const fallbackTrades = resolveChildTrades(descriptions, [], taskList, isDark);
      const customSheetIndex = delivery?.payload?.sheetIndex?.length
        ? delivery.payload.sheetIndex
        : extractSheetIndexFromDescriptions(descriptions);
      const subDeliveries = delivery?.subDeliveries || delivery?.payload?.subDeliveries || delivery?.combinedDeliveries || delivery?.payload?.combinedDeliveries;
      const defaultSubTitle = computeDefaultSubTitle(stageName, descriptions, subDeliveries, actualStageName);
      const deliveryId = resolveId(delivery);
      const subList: (DeliveryData | null)[] = Array.isArray(subDeliveries) && subDeliveries.length > 0 ? subDeliveries : [null];

      subList.forEach((subItem) => {
        const rawSubStage =
          (typeof subItem?.stageId === 'object' && subItem?.stageId
            ? subItem.stageId.temporaryName || subItem.stageId.name
            : '') ||
          subItem?.temporaryName ||
          subItem?.stageName ||
          subItem?.title;
        const subDescRaw = subItem
          ? subItem?.payload?.deliveryNotes ||
            subItem?.payload?.deliveryNotesToDisplay ||
            subItem?.payload?.Description ||
            subItem?.Description
          : null;
        const subTitle = subDescRaw && Array.isArray(subDescRaw) && subDescRaw.length > 0
          ? computeDefaultSubTitle(rawSubStage || stageName, subDescRaw, undefined, actualStageName)
          : (rawSubStage ? (/delivery/i.test(rawSubStage) ? rawSubStage : `${rawSubStage} Delivery`) : defaultSubTitle);
        const rawSubDate =
          subItem?.payload?.deliveredDate ||
          subItem?.payload?.deliveryDate ||
          subItem?.deliveredDate;
        const subDate = rawSubDate ? moment(rawSubDate).format('MMM DD, YYYY') : deliveryDate;
        const subGd = subItem ? extractGdLinks(subItem) : [];
        const subPdf = subItem ? extractPdfLinks(subItem) : [];
        const finalGd = subGd.length ? subGd : parentGdLinks;
        const subFrontApp = subItem ? extractFrontAppLinks(subItem) : [];
        const finalFrontApp = subFrontApp.length ? subFrontApp : parentFrontAppLinks;
        const finalPdf = subPdf.length ? subPdf : parentPdfLinks;
        const subDescItems = subDescRaw ? mapDescriptionItems(subDescRaw, taskList, subDate, rawSubStage || stageName, isDark) : null;
        const childTrades = subDescRaw ? resolveChildTrades(subDescRaw, fallbackTrades, taskList, isDark) : fallbackTrades;
        const subSheets = subDescRaw ? extractSheetIndexFromDescriptions(subDescRaw) : null;

        subRows.push({
          id: (subItem && resolveId(subItem)) || `${deliveryId || parentId}_sub_${subCounter}`,
          number: `${parentNum}.${subCounter}`,
          drawingTitle: subTitle,
          stageColor: subItem?.stageId?.color || (subItem as { stageColor?: string } | null)?.stageColor || stageColor,
          batchType: stageName,
          trades: childTrades,
          deliveryDate: subDate,
          status: 'Published',
          pdfLink: finalPdf[0] || parentPdf || '',
          gdLinks: finalGd,
          frontAppLinks: finalFrontApp,
          pdfLinks: finalPdf,
          rawDescriptions: subDescItems?.length ? subDescItems : descItems,
          sheetIndex: subSheets?.length ? subSheets : customSheetIndex,
          isChildRow: true,
          parentId,
        });
        subCounter++;
      });
    });

    const stageIds = new Set<string>();
    const stageNames = new Set<string>();

    if (group.stageKey) stageIds.add(group.stageKey);
    if (parentId) stageIds.add(parentId);
    if (stageName) stageNames.add(stageName.trim().toLowerCase());
    if (actualStageName) stageNames.add(actualStageName.trim().toLowerCase());

    group.deliveries.forEach((delivery) => {
      const sId = resolveId(delivery?.stageId);
      if (sId) stageIds.add(sId);
      const sdId = resolveId(delivery?.stageDataId);
      if (sdId) stageIds.add(sdId);
      const dId = resolveId(delivery);
      if (dId) stageIds.add(dId);

      const sName =
        delivery?.stageId?.temporaryName ||
        delivery?.temporaryName ||
        delivery?.stageId?.name ||
        delivery?.stageName;
      if (sName) stageNames.add(sName.trim().toLowerCase());
      if (delivery?.stageId?.name) stageNames.add(delivery.stageId.name.trim().toLowerCase());
      if (delivery?.stageName) stageNames.add(delivery.stageName.trim().toLowerCase());

      const subDeliveries =
        delivery?.subDeliveries ||
        delivery?.payload?.subDeliveries ||
        delivery?.combinedDeliveries ||
        delivery?.payload?.combinedDeliveries;
      if (Array.isArray(subDeliveries)) {
        subDeliveries.forEach((sub) => {
          const subId = resolveId(sub?.stageId) || resolveId(sub?.stageDataId) || resolveId(sub);
          if (subId) stageIds.add(subId);
          const subName =
            (typeof sub?.stageId === 'object' && sub?.stageId
              ? sub.stageId.temporaryName || sub.stageId.name
              : '') ||
            sub?.temporaryName ||
            sub?.stageName ||
            sub?.title;
          if (subName) stageNames.add(subName.trim().toLowerCase());
          if (sub?.stageId?.name) stageNames.add(sub.stageId.name.trim().toLowerCase());
          if (sub?.stageName) stageNames.add(sub.stageName.trim().toLowerCase());
        });
      }
    });

    const stageTasks = taskList.filter((task) => {
      const taskStageDataId = resolveId(task?.stageDataId);
      const taskStageId = resolveId(task?.stageId);
      const taskStageObj = typeof task?.stageId === 'object' && task?.stageId !== null ? (task.stageId as Record<string, unknown>) : null;
      const taskStageName = String(
        taskStageObj?.temporaryName || taskStageObj?.name || task?.stageName || ''
      ).trim().toLowerCase();

      return (
        (taskStageDataId !== '' && stageIds.has(taskStageDataId)) ||
        (taskStageId !== '' && stageIds.has(taskStageId)) ||
        (taskStageName !== '' && stageNames.has(taskStageName))
      );
    });

    const seenCodes = new Set<string>();
    const parentTrades: TradeItem[] = [];

    stageTasks.forEach((t) => {
      const taskIdObj = typeof t?.taskId === 'object' && t?.taskId !== null ? (t.taskId as Record<string, unknown>) : null;
      const tName = String(t?.name || t?.taskName || taskIdObj?.name || '');
      const tTaskTypeName = taskIdObj ? String(taskIdObj.name || '') : '';
      const code = getTradeCode(tName) || getTradeCode(tTaskTypeName);
      const normName = normalizeTradeName(tName) || normalizeTradeName(tTaskTypeName);
      const displayName =
        normName ||
        (code === 'M' ? 'Mechanical' : code === 'E' ? 'Electrical' : code === 'P' ? 'Plumbing' : code === 'SD' ? 'Structural Design' : code === 'S' ? 'Structural' : tName);

      const tradeKey = code || displayName.trim().toLowerCase();
      if (!tradeKey || seenCodes.has(tradeKey)) return;
      seenCodes.add(tradeKey);

      let color = String(t?.color || taskIdObj?.color || '');
      if (!color || color === '#000000' || color === '#000') {
        if (code === 'M') color = '#10B981';
        else if (code === 'E') color = '#F59E0B';
        else if (code === 'P') color = '#3B82F6';
        else if (code === 'SD' || code === 'S') color = '#8B5CF6';
        else color = isDark ? '#BB86FC' : '#7743DB';
      }

      parentTrades.push({
        name: displayName,
        color,
        code: code || undefined,
      });
    });

    subRows
      .flatMap((r) => r.trades || [])
      .forEach((t) => {
        const c = t.code || getTradeCode(t.name);
        const k = c || t.name.trim().toLowerCase();
        if (!k || seenCodes.has(k)) return;
        seenCodes.add(k);
        parentTrades.push(t);
      });

    // stage row date: the latest stageDeliveredDate among the stage's deliveries; blank while none has one
    const latestStageDeliveredDate = group.deliveries
      .map(d => d?.stageDeliveredDate)
      .filter((d): d is string => Boolean(d) && moment(d).isValid())
      .sort((a, b) => moment(b).valueOf() - moment(a).valueOf())[0];

    return {
      id: parentId,
      number: parentNum,
      drawingTitle: stageName,
      stageColor,
      batchType: stageName,
      trades: parentTrades,
      deliveryDate: latestStageDeliveredDate ? moment(latestStageDeliveredDate).format('MMM DD, YYYY') : '',
      status: 'Published',
      pdfLink: subRows.find(r => r.pdfLink)?.pdfLink || '',
      gdLinks: subRows.find(r => r.gdLinks?.length)?.gdLinks || [],
      frontAppLinks: subRows.find(r => r.frontAppLinks?.length)?.frontAppLinks || [],
      pdfLinks: subRows.find(r => r.pdfLinks?.length)?.pdfLinks || [],
      rawDescriptions: subRows.flatMap(r => r.rawDescriptions || []),
      sheetIndex: subRows.find(r => r.sheetIndex?.length)?.sheetIndex || [],
      subRows,
    };
  });
};
